import { useMemo, useCallback } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

const runCallback = (cb, val) => (
    typeof cb === "function" ? cb(val) : cb
);


function createStoreFinder(name) {
    return function (...keys) {
        return useSelector(slice=> {
            const state = slice[name] ?? slice;
            
            if (!keys.length) return state;

            const out = {};

            for (const key of keys) {
                const [path, name] = key.split(":");
                const parts = path.split(".");

                let temp = state;
                for (let p of parts) {
                    if (temp === undefined || temp === null) {
                        temp = undefined;
                        break;
                    }
                    temp = temp[p];
                }
                out[name || parts.at(-1)] = temp;
            }
            return out;

        }, shallowEqual);
    };
}


function createStoreHandler(defaultStates, customHandler = {}) {
    return function (...keys) {

        const dispatch = useDispatch();

        const dispatchAction = useCallback((type, key, callback) => {
            dispatch({ type, key, callback });
        }, [dispatch]);

        const memokey = keys.join('-');

        return useMemo(() => {

            let effectiveKeys = keys.length === 0 ? Object.keys(defaultStates) : keys;

            const handlers = {
                reset: () => dispatchAction('reset', null, null)
            };

            for (const key of effectiveKeys) {
                const [keyStr, name = `${key}Handler`] = key.split(':');

                handlers[name] = {
                    ...Object.keys(customHandler).reduce((acc, k) => {
                        acc[k] = (...args) => dispatchAction('custom', keyStr, pre => customHandler[k](pre, ...args));
                        return acc;
                    }, {}),

                    set: (callback) => dispatchAction('set', keyStr, callback),

                    update: (key, val) => {
                        const path = key.split(".");                        
                        dispatchAction('set', keyStr, pre => {
                            let temp = pre;
                            
                            let index = 0;
                            while(index < path.length-1) {
                                temp = temp[path[index++]] ?? {};
                            }

                            temp[path.at(-1)] = val;
                            return {...pre};
                        })
                    },

                    updateMany: (values = {}) => {
                        dispatchAction('set', keyStr, pre => ({ ...pre, ...values }))
                    }
                };
            }

            return handlers;

        }, [dispatchAction, defaultStates, memokey, customHandler]);
    };
}


export default function createStoreSlice({name, states: _states, customHandler = {}}) {

    const reducer = (states = _states, { type, key, callback }) => {
        if(type === 'reset') return _states;
        if (type !== 'set' && type !== 'custom') return states;

        const prevVal = states[key];
        const nextVal = runCallback(callback, prevVal);

        if (prevVal === nextVal) return states;

        return {
            ...states,
            [key]: nextVal
        };
    };

    const useStore = createStoreFinder(name);
    const useStoreHandlers = createStoreHandler(_states, customHandler);

    return {
        reducer,
        useStore,
        useStoreHandlers
    };
}